import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { styled, useTheme } from '@mui/material/styles';

import { ShieldCheck, PieChart, FileText, Activity } from 'lucide-react';
import Stack from '@mui/material/Stack';

const validationSchema = Yup.object({
  email: Yup.string().email('Indirizzo email non valido').required('Obbligatorio'),
  password: Yup.string().required('Obbligatorio'),
});

const RootStyle = styled(Grid)(({ theme }) => ({
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': {
    minHeight: '100dvh',
  },
  overflowX: 'hidden',
}));

const ImageSection = styled(Grid)(({ theme }) => ({
  backgroundImage: 'url(https://source.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80)', // Business/Architecture
  backgroundRepeat: 'no-repeat',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.88) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(10),
  color: theme.palette.common.white,
}));

const FeatureItem = ({ icon: Icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} color="#00AB55" /> {/* Secondary color for icon */}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 500 }}>
      {text}
    </Typography>
  </Stack>
);

const FormSection = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(2.5, 2, 4),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'center',
    padding: theme.spacing(8, 4),
  },
}));

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await login(values.email, values.password);
        navigate('/');
        toast.success('Accesso effettuato con successo');
      } catch (error) {
        const status = error?.response?.status;
        const msg = error?.response?.data?.message;
        const blockDate = error?.response?.data?.block_date;
        const userName = error?.response?.data?.user_name;
        const userEmail = error?.response?.data?.user_email;
        if (status === 403 && msg === 'Accesso bloccato') {
          const who = userName || userEmail || values.email;
          const when = blockDate ? new Date(blockDate).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
          toast.error(`${who} il tuo account è stato bloccato in data ${when} per informazioni contattare l'amministratore di sistema`);
        } else if (status === 403 && msg === 'Account non configurato') {
          const who = userName || userEmail || values.email;
          toast.error(`${who} non puoi accedere perchè non ti è stato assegnato un Ruolo e un Dipartimento. Contattare l'amministratore di sistema.`);
        } else {
          toast.error(msg || 'Accesso fallito');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <RootStyle container component="main">
      <CssBaseline />
      <ImageSection item md={7} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Overlay>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: 'common.white' }}>
            Benvenuto in DB
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 400, opacity: 0.8, mb: 8, maxWidth: 480 }}>
            La piattaforma professionale per la gestione integrata del debito bancario e delle pratiche legali.
          </Typography>

          <Box>
            <FeatureItem icon={Activity} text="Monitoraggio Pratiche in Tempo Reale" />
            <FeatureItem icon={PieChart} text="Analisi Finanziaria Avanzata" />
            <FeatureItem icon={FileText} text="Gestione Documentale Integrata" />
            <FeatureItem icon={ShieldCheck} text="Sicurezza e Compliance Normativa" />
          </Box>
        </Overlay>
      </ImageSection>
      <Grid item xs={12} sm={12} md={5} component={Paper} elevation={6} square sx={{ overflowY: 'auto' }}>
        <FormSection>
          <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
            <img
              src="/logo.svg"
              alt="DB Logo"
              style={{
                width: 'clamp(96px, 24vw, 160px)',
                height: 'clamp(96px, 24vw, 160px)',
                filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }}
            />
          </Box>
          <Typography component="h1" variant="h5">
            Accedi
          </Typography>
          <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ mt: 1, width: '100%', maxWidth: 400 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Indirizzo Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Accesso in corso...' : 'Accedi'}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Password dimenticata?
                </Link>
              </Grid>
              <Grid item>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Non hai un account? Registrati"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </FormSection>
      </Grid>
    </RootStyle>
  );
}
